import { useEffect } from "react";
import { RefabricProURL } from "api";
import userService from "api/user/user.service";
import { getAuthTokenFromLocalStorage, LocalStorageAuthToken, setAuthTokenFromLocalStorage } from "hooks/useAuthToken";
import { EventDispatchLogout } from "router/events";
import { useAppSelector } from "store";
import { getJwtSubject } from "utils/jwt";
import { createSessionRefresher, SessionRefreshResult } from "utils/sessionRefresh";

const tokenFrom = (responseData: unknown): SessionRefreshResult => {
  const token = (responseData as { token?: unknown } | null | undefined)?.token;

  return typeof token === "string" && token ? token : null;
};

type RefreshError = { response?: { status?: number; data?: { responseData?: unknown } } } | null;

/**
 * One call to `POST /users/refresh_token` with the current (still live) token. It runs in the
 * background, so it goes OUT of the shared interceptor (`skipGlobalErrorHandling`): a 402 paywall or a
 * 503 "servers busy" snackbar the user didn't ask for, at a random moment, would be the bug.
 *
 * - 200 → the fresh token.
 * - 402 → an account whose plan lapsed still gets a fresh token in the body (BE contract,
 *   `docs/refresh_token_auth.md` "st == 3 still refreshes"); it is read from the rejection.
 * - 401/403 → the server says this session is gone: end it the way any other request's 401 does.
 * - anything else (network, 429, 5xx) → no token; the scheduler retries quietly.
 *
 * The body email is only a cross-check on the fixed BE (identity is the token's `sub`) and REQUIRED by
 * the unfixed one, so it is taken from the token's own `sub`: it can never disagree with it.
 */
const refreshSessionToken = async (email: string): Promise<SessionRefreshResult> => {
  try {
    const res = await userService.refreshTokenInBackground({ email });

    return tokenFrom(res?.responseData);
  } catch (error) {
    const response = (error as RefreshError)?.response;
    if (response?.status === 401 || response?.status === 403) {
      window.dispatchEvent(new CustomEvent(EventDispatchLogout));

      return null;
    }

    return tokenFrom(response?.data?.responseData);
  }
};

const applyToken = (token: string) => {
  setAuthTokenFromLocalStorage(token);
  RefabricProURL.setAuthToken(token);
};

/**
 * Renews the access token before it expires, for as long as the user is signed in. Without it a
 * session simply dies 24 hours after login — mid-demo, mid-generation — because the BE refuses to
 * refresh a token that has already expired. See `utils/sessionRefresh`.
 */
const useSessionRefresh = () => {
  const isAuthorized = useAppSelector((state) => state.common.isAuthorized);

  useEffect(() => {
    if (!isAuthorized) return;

    // Anchor to the ACCOUNT this tab signed in as ([SELECTION_ANCHOR_BY_IDENTITY]). localStorage is
    // shared by every tab, so another tab may log out and in as someone else; this tab must neither
    // renew that token nor start sending it while it still shows the first account's data.
    const sessionSub = getJwtSubject(getAuthTokenFromLocalStorage());
    if (!sessionSub) return;
    const getSessionToken = () => {
      const token = getAuthTokenFromLocalStorage();

      return token && getJwtSubject(token) === sessionSub ? token : null;
    };

    const refresher = createSessionRefresher({ getToken: getSessionToken, applyToken, refresh: refreshSessionToken });

    // A sleeping laptop or a throttled background tab may miss the planned time: re-plan on wake.
    const onWake = () => {
      if (document.visibilityState === "visible") refresher.check();
    };
    // Another tab renewed the shared token: send it from THIS tab too (the HTTP client keeps its own
    // in-memory copy), then re-plan from its new `exp`. A token for a DIFFERENT account is ignored —
    // this tab keeps its own credential, exactly as it did before session renewal existed.
    const onStorage = (event: StorageEvent) => {
      if (event.key !== LocalStorageAuthToken) return;
      const token = getSessionToken();
      if (token) RefabricProURL.setAuthToken(token);
      refresher.check();
    };

    refresher.check();
    document.addEventListener("visibilitychange", onWake);
    window.addEventListener("focus", onWake);
    window.addEventListener("online", onWake);
    window.addEventListener("storage", onStorage);

    return () => {
      refresher.stop();
      document.removeEventListener("visibilitychange", onWake);
      window.removeEventListener("focus", onWake);
      window.removeEventListener("online", onWake);
      window.removeEventListener("storage", onStorage);
    };
  }, [isAuthorized]);
};

export default useSessionRefresh;
